/**
 * Convite para INSTALAR o app (PWA) — pedido do Lucas, 11/08.
 *
 * Aparece na primeira visita; depois que a pessoa instala, nunca mais. O que
 * o navegador nos dá para saber disso:
 *
 * - Android/Chrome/desktop: o evento `beforeinstallprompt` só dispara se o
 *   app é instalável E ainda não está instalado — seguramos o evento e
 *   mostramos o convite com um botão que chama o prompt de verdade (o
 *   navegador exige gesto). `appinstalled` confirma e grava a marca.
 * - iPhone/iPad: a Apple não tem prompt programático — o convite vira o
 *   passo-a-passo do "Adicionar à Tela de Início". "Entendi" grava a marca
 *   (não há como detectar a instalação do Safari).
 * - Já rodando instalado (display-mode standalone): grava a marca e sai.
 *
 * "Agora não" adia só a sessão (sessionStorage) — o convite volta na próxima
 * visita, porque a instalação continua valendo a pena; só a instalação (ou o
 * "Entendi" do iPhone) cala de vez.
 */

const MARCA = 'pwa-instalado';
const ADIADO = 'pwa-convite-adiado';
/** "Não mostrar de novo" — a pessoa recusou de vez, sem instalar (11/08, Lucas). */
const NUNCA = 'pwa-convite-nunca';

interface EventoInstalar extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function rodandoInstalado(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function cartao(html: string): HTMLElement {
  const c = document.createElement('div');
  c.className = 'pwa-convite';
  c.innerHTML = `
    <img src="/marca/icone-192.png" alt="">
    <div class="pwa-texto">${html}</div>
    <div class="pwa-botoes">
      <button class="btn" id="pwa-sim" type="button">📲 Instalar</button>
      <button class="btn btn-claro" id="pwa-depois" type="button">Agora não</button>
    </div>
    <button class="pwa-nunca" id="pwa-nunca" type="button">Não mostrar essa mensagem de novo</button>`;
  document.body.appendChild(c);
  c.querySelector('#pwa-nunca')!.addEventListener('click', () => {
    localStorage.setItem(NUNCA, '1');
    c.remove();
  });
  return c;
}

export function instalarConvitePwa(): void {
  if (rodandoInstalado()) {
    // A pessoa abriu o app instalado — a marca fica e o convite morre aqui.
    localStorage.setItem(MARCA, '1');
    return;
  }
  if (localStorage.getItem(MARCA) || localStorage.getItem(NUNCA) || sessionStorage.getItem(ADIADO)) {
    return;
  }

  window.addEventListener('appinstalled', () => {
    localStorage.setItem(MARCA, '1');
    document.querySelector('.pwa-convite')?.remove();
  });

  const ehIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  if (ehIos) {
    const c = cartao(
      `<b>Instale o Sistema CD no seu iPhone</b>
       Toque em <b>compartilhar</b> (□↑) e depois em <b>“Adicionar à Tela de Início”</b>.
       O app abre em tela cheia e recebe os avisos.`,
    );
    const b = c.querySelector<HTMLButtonElement>('#pwa-sim')!;
    b.textContent = '✓ Entendi';
    b.addEventListener('click', () => {
      // Não há como detectar a instalação no Safari — o "Entendi" é a marca.
      localStorage.setItem(MARCA, '1');
      c.remove();
    });
    c.querySelector('#pwa-depois')!.addEventListener('click', () => {
      sessionStorage.setItem(ADIADO, '1');
      c.remove();
    });
    return;
  }

  // Android/desktop: o evento só vem se dá para instalar E ainda não está.
  // O texto fala do aparelho CERTO — "celular" numa tela de computador soava
  // errado (reparo do Lucas, 11/08).
  const noCelular = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    // Reconferir as marcas AQUI: o navegador pode disparar de novo na mesma
    // página, depois de a pessoa já ter dito "não mostrar de novo".
    if (localStorage.getItem(MARCA) || localStorage.getItem(NUNCA)) return;
    if (document.querySelector('.pwa-convite')) return;
    const pedido = e as EventoInstalar;
    const c = cartao(
      noCelular
        ? `<b>Instale o Sistema CD no celular</b>
           Abre direto da tela inicial, em tela cheia, com os avisos de romaneio e manutenção.`
        : `<b>Instale o Sistema CD no computador</b>
           Vira um atalho na área de trabalho e abre em janela própria, sem as abas do navegador.`,
    );
    c.querySelector('#pwa-sim')!.addEventListener('click', () => {
      void pedido.prompt().then(() => pedido.userChoice).then((escolha) => {
        if (escolha.outcome === 'accepted') localStorage.setItem(MARCA, '1');
        c.remove();
      });
    });
    c.querySelector('#pwa-depois')!.addEventListener('click', () => {
      sessionStorage.setItem(ADIADO, '1');
      c.remove();
    });
  });
}
